package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.AdminRepository
import com.arenaproplay.admin.domain.admin.Period
import com.arenaproplay.admin.domain.admin.SessionRow
import com.arenaproplay.admin.domain.admin.SessionStep
import com.arenaproplay.admin.domain.admin.TimelineEvent
import com.arenaproplay.admin.ui.components.widgets.CardSpinner
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.mono
import com.arenaproplay.admin.ui.utils.countryFlag
import com.arenaproplay.admin.ui.utils.countryNameRu
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.jetbrains.compose.web.css.*

// 10 строк — секция заметна, но KPI остаются в зоне видимости без длинного скролла
private const val PAGE_SIZE = 10

// ─────────────────────────────────────────────────────────────────────────────
// Shared event-label helper (extracted so SessionDrawer doesn't duplicate it)
// ─────────────────────────────────────────────────────────────────────────────

internal fun isScreenEventType(eventType: String): Boolean =
    eventType.lowercase().let { it == "screen_view" || it == "screen" || it.startsWith("screen.") }

internal fun humanizeEventLabel(event: TimelineEvent): String {
    val type = event.eventType
    return when {
        isScreenEventType(type) && event.screen != null -> "Экран: ${event.screen}"
        isScreenEventType(type) -> "Переход (экран неизвестен)"
        type.startsWith("click.") -> "Действие: ${type.removePrefix("click.")}"
        else -> humanizeEventType(type)
    }
}

/** Known backend business events (TrackedEvent) — по-русски. Shared with StepPath EVENT chips. */
internal fun humanizeEventType(type: String): String = when {
    type == "session_start" -> "Начало сессии"
    type == "app_open" -> "Открыл приложение"
    type == "app_background" -> "Свернул приложение"
    type == "user.signup" -> "Регистрация"
    type == "user.login" -> "Вход"
    type == "booking.created" -> "Создал бронирование"
    type == "event.joined" -> "Записался на событие"
    type == "event.left" -> "Покинул событие"
    type.startsWith("wallet.") -> "Платёж: ${type.removePrefix("wallet.")}"
    type.startsWith("subscription") -> "Подписка: ${type.substringAfter('.', type)}"
    else -> type
}

// ─────────────────────────────────────────────────────────────────────────────
// Duration formatter
// ─────────────────────────────────────────────────────────────────────────────

private fun formatDuration(seconds: Long): String = when {
    seconds < 60  -> "${seconds} сек"
    seconds < 3600 -> "${seconds / 60} мин"
    else -> {
        val h = seconds / 3600
        val m = (seconds % 3600) / 60
        if (m == 0L) "${h} ч" else "${h} ч ${m} м"
    }
}

/** Kontaq clock-style duration: «2:34» (minutes:seconds, zero-padded). */
private fun formatDurationClock(totalSeconds: Long): String {
    val m = totalSeconds / 60
    val s = totalSeconds % 60
    return "$m:${s.toString().padStart(2, '0')}"
}

// ─────────────────────────────────────────────────────────────────────────────
// Relative timestamp (epoch ms → «2 мин назад» style)
// ─────────────────────────────────────────────────────────────────────────────

private fun formatRelative(epochMs: Long): String {
    val diffMs = Date.now().toLong() - epochMs
    val diffSec = diffMs / 1000
    return when {
        diffSec < 60       -> "только что"
        diffSec < 3600     -> "${diffSec / 60} мин назад"
        diffSec < 86400    -> "${diffSec / 3600} ч назад"
        diffSec < 2592000  -> "${diffSec / 86400} д назад"
        else               -> {
            val d = Date(epochMs.toDouble())
            val months = listOf("янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек")
            "${d.getDate()} ${months[d.getMonth()]}"
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Session event-time formatter (HH:mm:ss, local browser timezone)
// ─────────────────────────────────────────────────────────────────────────────

private fun formatSessionTime(ms: Long): String {
    val d = Date(ms.toDouble())
    val h = d.getHours().toString().padStart(2, '0')
    val m = d.getMinutes().toString().padStart(2, '0')
    val s = d.getSeconds().toString().padStart(2, '0')
    return "$h:$m:$s"
}

/** Full local date+time (for the drawer header). */
private fun formatLocalDateTime(ms: Long): String {
    val d = Date(ms.toDouble())
    val months = listOf("янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек")
    val day = d.getDate().toString().padStart(2, '0')
    val mon = months[d.getMonth()]
    val year = d.getFullYear()
    val h = d.getHours().toString().padStart(2, '0')
    val min = d.getMinutes().toString().padStart(2, '0')
    return "$day $mon $year · $h:$min"
}

// ─────────────────────────────────────────────────────────────────────────────
// Platform color
// ─────────────────────────────────────────────────────────────────────────────

private fun platformColorHex(platform: String): String = when (platform.lowercase()) {
    "ios"     -> "#0BAB64"
    "android" -> "#C7D2FE"
    "web"     -> "#4D8CFF"
    else      -> "#6F727A"
}

// ─────────────────────────────────────────────────────────────────────────────
// Anonymous avatar seed helper
// ─────────────────────────────────────────────────────────────────────────────

private fun anonDevicePrefix(deviceId: String?): String =
    deviceId?.take(8) ?: "??????"

// ─────────────────────────────────────────────────────────────────────────────
// Section error helper (private, scoped to this file)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SectionError(message: String) {
    SpanText(
        "⚠ $message",
        Modifier.bodySRegular().styleModifier { property("color", "#FF6B5B") }
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Public entry point — «Сессии» GlassCard
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Kontaq-style sessions list for the Behavior tab.
 *
 * Shows who was in the session, where they came from (country), the platform,
 * how long they were active, and how many actions / screens they visited.
 * Clicking a row opens a [SessionEventsDrawer] with a chronological step list.
 */
@Composable
fun SessionListSection(
    period: Period,
    repo: AdminRepository,
) {
    var sessions    by remember(period) { mutableStateOf<List<SessionRow>?>(null) }
    var total       by remember(period) { mutableStateOf(0L) }
    var offset      by remember(period) { mutableStateOf(0) }
    var loading     by remember(period) { mutableStateOf(false) }
    var error       by remember(period) { mutableStateOf<String?>(null) }
    var retryToken  by remember { mutableStateOf(0) }

    // Selected session for the drawer
    var drawerSession by remember { mutableStateOf<SessionRow?>(null) }
    // Смена периода перезагружает список — открытый drawer от старого периода закрываем
    LaunchedEffect(period) { drawerSession = null }

    val scope = rememberCoroutineScope()

    // Load (or reload) whenever period, offset, or retryToken changes
    LaunchedEffect(period, offset, retryToken) {
        error = null
        loading = true
        runCatching { repo.getSessions(period, PAGE_SIZE, offset) }
            .onSuccess { list ->
                sessions = list.items
                total    = list.total
            }
            .onFailure { error = it.message?.takeIf { it.isNotBlank() } ?: "Ошибка загрузки" }
        loading = false
    }

    GlassCard(radius = 16, padding = 20) {
        // ── Header ────────────────────────────────────────────────────────────
        Row(
            Modifier.fillMaxWidth()
                .styleModifier {
                    property("align-items", "center")
                    property("justify-content", "space-between")
                }
        ) {
            Column {
                SpanText("СЕССИИ", Modifier.eyebrow().color(AppColors.Faint))
                if (total > 0 && sessions != null) {
                    Box(Modifier.height(2.px))
                    SpanText(
                        "$total сессий за период",
                        Modifier.bodySRegular().color(AppColors.Faint),
                    )
                }
            }
        }

        VSpace(14)

        // ── Body ──────────────────────────────────────────────────────────────
        when {
            error != null -> {
                SectionError(error!!)
                VSpace(8)
                Box(
                    Modifier
                        .sBorderRadius(8)
                        .sPadding(8, 16)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property("background", "rgba(255,255,255,.06)")
                            property("display", "inline-flex")
                            property("align-items", "center")
                        }
                        .classNames("na-press")
                        .onClick { scope.launch { retryToken++ } }
                ) {
                    SpanText("Повторить", Modifier.bodySMedium().color(AppColors.Ink))
                }
            }

            loading && sessions == null -> {
                Box(Modifier.fillMaxWidth().height(120.px), contentAlignment = Alignment.Center) {
                    CardSpinner()
                }
            }

            sessions != null && sessions!!.isEmpty() -> {
                EmptyState(
                    icon  = "📂",
                    title = "Нет сессий за период",
                    hint  = "Сессий за период нет — проверь, что мобильное приложение шлёт события",
                )
            }

            sessions != null -> {
                // Session rows
                Column(Modifier.fillMaxWidth()) {
                    sessions!!.forEachIndexed { idx, session ->
                        SessionRowItem(
                            session   = session,
                            isLast    = idx == sessions!!.size - 1,
                            onClick   = { drawerSession = session },
                        )
                    }
                }

                // ── Pagination ─────────────────────────────────────────────
                val hasPrev = offset > 0
                val hasNext = (offset + PAGE_SIZE) < total

                if (hasPrev || hasNext) {
                    VSpace(12)
                    Row(
                        Modifier.fillMaxWidth()
                            .styleModifier {
                                property("align-items", "center")
                                property("justify-content", "space-between")
                            }
                    ) {
                        if (hasPrev) {
                            PagerButton("← Назад") {
                                scope.launch { offset = (offset - PAGE_SIZE).coerceAtLeast(0) }
                            }
                        } else {
                            Box(Modifier.width(80.px))
                        }

                        val page = offset / PAGE_SIZE + 1
                        val totalPages = ((total + PAGE_SIZE - 1) / PAGE_SIZE).toInt().coerceAtLeast(1)
                        SpanText(
                            "$page / $totalPages",
                            Modifier.bodySRegular().color(AppColors.Faint),
                        )

                        if (hasNext) {
                            PagerButton("Ещё →") {
                                scope.launch { offset += PAGE_SIZE }
                            }
                        } else {
                            Box(Modifier.width(80.px))
                        }
                    }
                }

                // Loading indicator while paginating
                if (loading) {
                    VSpace(8)
                    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                        CardSpinner()
                    }
                }
            }
        }
    }

    // ── Drawer ────────────────────────────────────────────────────────────────
    val session = drawerSession
    SessionEventsDrawer(
        session = session,
        open    = session != null,
        onClose = { drawerSession = null },
        repo    = repo,
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Session row
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SessionRowItem(
    session: SessionRow,
    isLast: Boolean,
    onClick: () -> Unit,
) {
    val isAnon       = session.userId == null
    val avatarSeed   = session.userId ?: session.deviceId ?: session.sessionId
    val avatarLabel  = if (isAnon) {
        "?" // single char so it fits the bubble
    } else {
        session.userName
            ?.split(" ", limit = 2)
            ?.mapNotNull { it.firstOrNull()?.uppercaseChar()?.toString() }
            ?.take(2)
            ?.joinToString("")
            ?.takeIf { it.isNotBlank() }
            ?: "U"
    }

    // Аноним = «Гость»: гео уже показано рядом с флагом в Row1 — не дублируем
    // и не спрягаем («Гость из Россия» было и неграмотно, и излишне).
    val hasGeo = session.city != null || session.country != null
    val displayName = if (isAnon) {
        if (hasGeo) "Гость" else "Гость · ${anonDevicePrefix(session.deviceId)}"
    } else {
        session.userName ?: "User"
    }

    val platformColor = platformColorHex(session.platform)

    // Kontaq SessionCard anatomy (spec):
    //   Row1: [flag 16px] [city-or-country 13px semibold] [имя dim — наша добавка]
    //         [platform] [DeviceChip «Desktop · Chrome»] [SourceChip] [spacer]
    //         [«Новый»] [«Онлайн» pulsing] [«2:34 · 5 мин назад» dim]
    //   Row2: StepPath — горизонтальный скролл чипов с → стрелками
    Row(
        Modifier
            .fillMaxWidth()
            .sPadding(10, 12)
            .sBorderRadius(10)
            .cursor(Cursor.Pointer)
            .classNames("na-press")
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid rgba(255,255,255,.06)")
                property("transition", "background 140ms ease")
                property("align-items", "flex-start")
                property("gap", "12px")
            }
            .onClick { onClick() }
    ) {
        // Avatar (наша identity-добавка к kontaq-анатомии)
        AvatarBubble(seed = avatarSeed, initials = avatarLabel, sizePx = 32)

        Column(Modifier.weight(1f).styleModifier { property("min-width", "0"); property("gap", "6px") }) {
            // ── Row 1: geo + chips + badges + время ──────────────────────────
            Row(
                Modifier.fillMaxWidth()
                    .styleModifier {
                        property("align-items", "center")
                        property("gap", "8px")
                        property("flex-wrap", "wrap")
                    }
            ) {
                // [1] Flag 16px
                if (session.country != null) {
                    SpanText(
                        countryFlag(session.country),
                        Modifier.styleModifier { property("font-size", "16px"); property("flex-shrink", "0") }
                    )
                }
                // [2] Geo label: city → countryName → «—»
                val geoLabel = session.city
                    ?: session.country?.let { countryNameRu(it) }
                    ?: "—"
                SpanText(
                    geoLabel,
                    Modifier.styleModifier {
                        property("font-size", "13px")
                        property("font-weight", "600")
                        property("color", "#F0F4FC")
                        property("white-space", "nowrap")
                    }
                )
                // Имя юзера (dim) — сохраняем нашу идентификацию сессии
                SpanText(
                    displayName,
                    Modifier.styleModifier {
                        property("font-size", "11px")
                        property("color", if (isAnon) "rgba(240,244,252,.40)" else "rgba(240,244,252,.60)")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                        property("max-width", "160px")
                    }
                )
                // Platform pill (ios/android/web — наша добавка)
                SessionPlatformChip(session.platform, platformColor)
                // [3] DeviceChip «Desktop · Chrome» — glass chip; skip if both null
                val deviceParts = listOfNotNull(
                    session.device?.lowercase()?.replaceFirstChar { it.uppercase() },
                    session.browser,
                )
                if (deviceParts.isNotEmpty()) {
                    SessionStepChip(deviceParts.joinToString(" · "))
                }
                // [4] SourceChip: source ?? «прямой»
                SessionStepChip(session.source ?: "прямой")
                // [5] Spacer
                Box(Modifier.styleModifier { property("flex", "1") })
                // [6] «Новый» amber badge
                if (session.isNew) SessionNewBadge()
                // [7] «Онлайн» pulsing badge
                if (session.isOnline) SessionLiveBadge()
                // [8] «2:34 · 5 мин назад» dim
                SpanText(
                    "${formatDurationClock(session.durationSeconds)} · ${formatRelative(session.startedAt)}",
                    Modifier.styleModifier {
                        property("font-size", "11px")
                        property("color", "rgba(240,244,252,.45)")
                        property("white-space", "nowrap")
                    }
                )
            }

            // ── Row 2: StepPath (fallback — счётчики, если шагов нет) ────────
            if (session.steps.isNotEmpty()) {
                StepPathRow(session.steps)
            } else {
                SpanText(
                    "${session.eventCount} действий · ${session.screensCount} экранов",
                    Modifier.mono(11).color(AppColors.Faint),
                )
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Status badges (kontaq Badge / LiveBadge, NA tokens)
// ─────────────────────────────────────────────────────────────────────────────

/** «Новый» — amber pill (наш жёлтый токен #E4CD40 вместо kontaq #E8A33D). */
@Composable
private fun SessionNewBadge() {
    Box(
        Modifier
            .sBorderRadius(999)
            .sPadding(3, 8)
            .styleModifier {
                property("background", "rgba(228,205,64,.13)")
                property("border", "1px solid rgba(228,205,64,.40)")
                property("display", "inline-flex")
                property("align-items", "center")
                property("flex-shrink", "0")
            }
    ) {
        SpanText(
            "Новый",
            Modifier.styleModifier {
                property("font-size", "10px")
                property("font-weight", "700")
                property("color", "#E4CD40")
            }
        )
    }
}

/** «Онлайн» — мятный пульсирующий бейдж (kontaq LiveBadge, na-pulse keyframe). */
@Composable
private fun SessionLiveBadge() {
    Row(
        Modifier
            .sBorderRadius(999)
            .sPadding(3, 8)
            .styleModifier {
                property("background", "rgba(0,229,164,.14)")
                property("border", "1px solid rgba(0,229,164,.40)")
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "5px")
                property("flex-shrink", "0")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier.width(7.px).height(7.px)
                .classNames("na-pulse")
                .styleModifier {
                    property("border-radius", "999px")
                    property("background", "#00E5A4")
                }
        )
        SpanText(
            "Онлайн",
            Modifier.styleModifier {
                property("font-size", "10px")
                property("font-weight", "700")
                property("color", "#00E5A4")
            }
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Step chip — kontaq .qp-step glass chip (11.5px, glass bg, thin border).
// Used for Device/Source chips and every step in StepPathRow.
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SessionStepChip(text: String, dim: Boolean = false) {
    Box(
        Modifier
            .sBorderRadius(8)
            .sPadding(5, 9)
            .styleModifier {
                property("background", "rgba(255,255,255,.05)")
                property("border", "1px solid rgba(255,255,255,.08)")
                property("display", "inline-flex")
                property("align-items", "center")
                property("flex-shrink", "0")
                property("white-space", "nowrap")
            }
    ) {
        SpanText(
            text,
            Modifier.styleModifier {
                property("font-size", "11.5px")
                property("color", if (dim) "rgba(240,244,252,.45)" else "rgba(240,244,252,.82)")
                property("white-space", "nowrap")
            }
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Step path row — kontaq StepPath: horizontal scroll (nowrap, hidden scrollbar),
// chips with → arrows; truncation first 6 + «+N» + last 4 when > 12 steps.
// ─────────────────────────────────────────────────────────────────────────────

/** Type-aware chip label: PAGEVIEW=screen, CLICK=«⊙ …», EVENT=по-русски. */
private fun stepChipLabel(step: SessionStep): String = when (step.type) {
    "PAGEVIEW" -> step.label
    "CLICK"    -> "⊙ ${step.label}"
    "EVENT"    -> humanizeEventType(step.label)
    else       -> step.label // MORE collapse marker etc.
}

@Composable
private fun StepPathRow(steps: List<SessionStep>) {
    if (steps.isEmpty()) return
    // Collapse if > 12 steps: first 6 + "+N" marker + last 4
    val shown = if (steps.size <= 12) steps
    else steps.take(6) + SessionStep("MORE", "+${steps.size - 10}") + steps.takeLast(4)

    Row(
        Modifier.fillMaxWidth()
            .classNames("no-scrollbar")
            .styleModifier {
                property("overflow-x", "auto")
                property("flex-wrap", "nowrap")
                property("align-items", "center")
                property("gap", "4px")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        shown.forEachIndexed { i, step ->
            if (i > 0) {
                SpanText(
                    "→",
                    Modifier.styleModifier {
                        property("font-size", "12px")
                        property("color", "rgba(240,244,252,.30)")
                        property("flex-shrink", "0")
                    }
                )
            }
            SessionStepChip(stepChipLabel(step), dim = step.type == "MORE")
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Platform chip (file-private, same style as BehaviorTab.PlatformChip)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SessionPlatformChip(platform: String, colorHex: String) {
    Box(
        Modifier
            .sBorderRadius(6)
            .sPadding(2, 7)
            .styleModifier {
                property("background", "${colorHex}22")
                property("display", "inline-flex")
                property("align-items", "center")
            }
    ) {
        SpanText(
            platform,
            Modifier.bodySMedium()
                .styleModifier {
                    property("color", colorHex)
                    property("font-size", "11px")
                    property("font-weight", "600")
                }
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Pager button
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun PagerButton(label: String, onClick: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(8)
            .sPadding(6, 14)
            .cursor(Cursor.Pointer)
            .classNames("na-press")
            .styleModifier {
                property("background", "rgba(255,255,255,.06)")
                property("display", "inline-flex")
                property("align-items", "center")
                property("transition", "background 140ms ease")
            }
            .onClick { onClick() }
    ) {
        SpanText(label, Modifier.bodySMedium().color(AppColors.Ink))
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Session events drawer
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SessionEventsDrawer(
    session: SessionRow?,
    open: Boolean,
    onClose: () -> Unit,
    repo: AdminRepository,
) {
    var events      by remember { mutableStateOf<List<TimelineEvent>?>(null) }
    var loading     by remember { mutableStateOf(false) }
    var error       by remember { mutableStateOf<String?>(null) }
    var retryToken  by remember { mutableStateOf(0) }
    val scope       = rememberCoroutineScope()

    LaunchedEffect(session?.sessionId, open, retryToken) {
        val sid = session?.sessionId ?: return@LaunchedEffect
        if (!open) return@LaunchedEffect
        events  = null
        error   = null
        loading = true
        runCatching { repo.getSessionEvents(sid) }
            .onSuccess { events = it }
            .onFailure { error = it.message?.takeIf { it.isNotBlank() } ?: "Ошибка загрузки" }
        loading = false
    }

    Drawer(open = open, onClose = onClose, title = "Сессия") {
        if (session == null) return@Drawer

        // ── Drawer header summary ─────────────────────────────────────────────
        val isAnon      = session.userId == null
        val displayName = if (isAnon) {
            if (session.city != null || session.country != null) "Гость"
            else "Гость · ${anonDevicePrefix(session.deviceId)}"
        } else {
            session.userName ?: "User"
        }

        Column(Modifier.fillMaxWidth()) {
            // Avatar + name row
            Row(
                Modifier.styleModifier {
                    property("align-items", "center")
                    property("gap", "12px")
                }
            ) {
                val avatarSeed   = session.userId ?: session.deviceId ?: session.sessionId
                val avatarLabel  = if (isAnon) "?" else {
                    session.userName
                        ?.split(" ", limit = 2)
                        ?.mapNotNull { it.firstOrNull()?.uppercaseChar()?.toString() }
                        ?.take(2)?.joinToString("")?.takeIf { it.isNotBlank() } ?: "U"
                }
                AvatarBubble(seed = avatarSeed, initials = avatarLabel, sizePx = 40)
                Column {
                    SpanText(
                        displayName,
                        Modifier.bodyMMedium().color(AppColors.Ink)
                            .styleModifier { property("font-weight", "600") }
                    )
                    Box(Modifier.height(2.px))
                    Row(Modifier.styleModifier { property("align-items", "center"); property("gap", "8px") }) {
                        if (session.country != null) {
                            SpanText(
                                "${countryFlag(session.country)} ${session.country}",
                                Modifier.bodySRegular().color(AppColors.Faint),
                            )
                        }
                        val pColor = platformColorHex(session.platform)
                        SessionPlatformChip(session.platform, pColor)
                    }
                }
            }

            VSpace(12)

            // Meta pills row
            Row(
                Modifier.fillMaxWidth()
                    .styleModifier {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("gap", "8px")
                    }
            ) {
                DrawerMetaChip("⏱ ${formatDuration(session.durationSeconds)}")
                DrawerMetaChip("${session.eventCount} действий")
                DrawerMetaChip("${session.screensCount} экранов")
            }

            VSpace(4)
            SpanText(
                "Начало: ${formatLocalDateTime(session.startedAt)}",
                Modifier.mono(11).color(AppColors.Faint),
            )
            VSpace(16)

            // Divider
            Box(
                Modifier.fillMaxWidth().height(1.px)
                    .styleModifier { property("background", "rgba(255,255,255,.08)") }
            )
            VSpace(16)

            // ── Event list ────────────────────────────────────────────────────
            when {
                loading -> {
                    Box(Modifier.fillMaxWidth().height(120.px), contentAlignment = Alignment.Center) {
                        CardSpinner()
                    }
                }
                error != null -> {
                    SpanText(
                        "⚠ ${error!!}",
                        Modifier.bodySRegular().styleModifier { property("color", "#FF6B5B") }
                    )
                    VSpace(8)
                    Box(
                        Modifier.sBorderRadius(8).sPadding(6, 14)
                            .cursor(Cursor.Pointer)
                            .classNames("na-press")
                            .styleModifier {
                                property("background", "rgba(255,255,255,.06)")
                                property("display", "inline-flex")
                            }
                            .onClick { scope.launch { retryToken++ } }
                    ) {
                        SpanText("Повторить", Modifier.bodySMedium().color(AppColors.Ink))
                    }
                }
                events != null && events!!.isEmpty() -> {
                    SpanText(
                        "Нет событий для этой сессии",
                        Modifier.bodySRegular().color(AppColors.Faint),
                    )
                }
                events != null -> {
                    Column(Modifier.fillMaxWidth().styleModifier { property("gap", "0") }) {
                        events!!.forEach { event ->
                            DrawerEventRow(event)
                        }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawer meta chip
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun DrawerMetaChip(text: String) {
    Box(
        Modifier
            .sBorderRadius(999)
            .sPadding(4, 12)
            .styleModifier {
                property("background", "rgba(255,255,255,.06)")
                property("border", "1px solid rgba(255,255,255,.08)")
                property("display", "inline-flex")
                property("align-items", "center")
            }
    ) {
        SpanText(text, Modifier.bodySMedium().color(AppColors.Ink))
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawer event row — chronological step list
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun DrawerEventRow(event: TimelineEvent) {
    Row(
        Modifier
            .fillMaxWidth()
            .sPadding(6, 0)
            .styleModifier {
                property("align-items", "flex-start")
                property("gap", "10px")
                property("border-bottom", "1px solid rgba(255,255,255,.04)")
                property("padding-bottom", "6px")
            }
    ) {
        // Time column — HH:mm:ss, fixed width, mono
        Box(Modifier.styleModifier { property("flex-shrink", "0"); property("min-width", "60px") }) {
            SpanText(
                formatSessionTime(event.occurredAt),
                Modifier.mono(11).color(AppColors.Faint),
            )
        }
        // Event label
        Column(Modifier.weight(1f).styleModifier { property("gap", "2px") }) {
            SpanText(
                humanizeEventLabel(event),
                Modifier.bodyMRegular().color(AppColors.Ink),
            )
            // Show screen name for non-screen events that have a screen context
            if (event.screen != null && !isScreenEventType(event.eventType)) {
                SpanText(
                    event.screen,
                    Modifier.mono(11).color(AppColors.Faint)
                        .styleModifier { property("color", "#4D8CFF") },
                )
            }
        }
    }
}
